import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart as RePieChart, Pie, Cell,
} from "recharts";
import { fmtTooltip, monthLabel, CHART_COLORS } from "@/lib/mis-utils";

export function MISCharts({ data }) {
  const pl = data.pl;
  const ms = data.monthly_summary || [];

  const monthlyBarData = ms.map((m) => ({
    month: monthLabel(m.month),
    Revenue: Math.round(m.revenue),
    "Direct Costs": Math.round(m.direct_costs),
    "Employee Costs": Math.round(m.employee_costs),
    Overheads: Math.round(m.overheads),
    "Finance Costs": Math.round(m.finance_costs),
  }));

  const costPieData = [
    { name: "Material/Purchase", value: Math.abs(pl.material_cost) },
    { name: "Other Direct", value: Math.abs(pl.other_direct_costs) },
    { name: "Employee", value: Math.abs(pl.total_employee_costs) },
    { name: "Overheads", value: Math.abs(pl.total_overheads) },
    { name: "Finance", value: Math.abs(pl.total_finance_costs) },
    { name: "Depreciation", value: Math.abs(pl.total_depreciation) },
  ].filter(d => d.value > 0);

  const waterfallData = [
    { name: "Revenue", value: pl.total_revenue, fill: "#059669" },
    { name: "Gross Profit", value: pl.gross_profit, fill: pl.gross_profit >= 0 ? "#0891b2" : "#dc2626" },
    { name: "EBITDA", value: pl.ebitda, fill: pl.ebitda >= 0 ? "#1e40af" : "#dc2626" },
    { name: "PBT", value: pl.pbt, fill: pl.pbt >= 0 ? "#7c3aed" : "#dc2626" },
  ];

  const customTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-slate-200 rounded p-2 shadow-lg text-xs">
        <p className="font-semibold text-slate-700 mb-1">{label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: p.color || p.fill }}>
            {p.name}: {fmtTooltip(p.value)}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="mis-charts-tab">
      {monthlyBarData.length > 0 && (
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Monthly Revenue vs Costs</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={monthlyBarData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={fmtTooltip} tick={{ fontSize: 10 }} />
                <Tooltip content={customTooltip} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Revenue" fill="#059669" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Direct Costs" fill="#dc2626" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Employee Costs" fill="#d97706" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Overheads" fill="#9333ea" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {costPieData.length > 0 && (
          <Card className="border border-slate-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">Cost Composition</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <ResponsiveContainer width="100%" height={280}>
                <RePieChart>
                  <Pie data={costPieData} cx="50%" cy="50%" outerRadius={100} innerRadius={45}
                    dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: "#94a3b8", strokeWidth: 1 }}>
                    {costPieData.map((entry, idx) => (
                      <Cell key={entry.name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => fmtTooltip(val)} />
                </RePieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">P&L Waterfall</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={waterfallData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={fmtTooltip} tick={{ fontSize: 10 }} />
                <Tooltip content={customTooltip} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {waterfallData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
